import { calculateExpression } from "../expression/expressionFunctions.js";

import type { BaseEffectItem, EffectItem, JoinedBaseEffectItem } from "./EffectItem.js";
import type { ImpulseItem } from "../impulse/ImpulseItem.js";
import type { ExpressionParseContext } from "./Expression.js";
import type { SetRequired } from "type-fest";

class Effect implements BaseEffectItem {
  id: number | string;
  type: "effect";
  createdAt: number;

  affectedId: number;
  affectedType: "target" | "resource";

  activatorId: number | string | null;
  activatorType: "incident" | "action" | "scheduledEffect";

  impulseExpressions: string[];

  hint: string;
  visibility: "hint" | "hidden" | "visible";
  pendingEffectsVisible: boolean;
  pendingEffectsOpenable: boolean;

  constructor(effectData: Omit<BaseEffectItem, "type">) {
    this.type = "effect";

    this.id = effectData.id;
    this.affectedType = effectData.affectedType;
    this.affectedId = effectData.affectedId;
    this.activatorType = effectData.activatorType;
    this.activatorId = effectData.activatorId;
    this.impulseExpressions = [...effectData.impulseExpressions];

    this.hint = effectData.hint;
    this.visibility = effectData.visibility;
    this.pendingEffectsVisible = effectData.pendingEffectsVisible;
    this.pendingEffectsOpenable = effectData.pendingEffectsOpenable;

    this.createdAt = effectData.createdAt;
  }

  /**
   * Creates an Impulse using the effect's first expression.
   * Removes the expression afterwards.
   */
  shiftImpulse(
    parseContext: SetRequired<ExpressionParseContext, "rounds" | "targets" | "resources" | "indices" | "roundIndex">
  ) {
    if (this.impulseExpressions.length == 0) return null;

    // generate an impulse from the first expression.
    const impulse = Effect.toImpulse(this, parseContext);

    // remove the expression afterwards
    this.impulseExpressions.shift();

    return impulse;
  }

  clone() {
    return new Effect(this);
  }

  isEmpty() {
    return this.impulseExpressions.length === 0;
  }

  /**
   * Create an impulse of an effect
   *
   * @param effectItem The effect of which to generate the impulse
   * @param round Provides the simulation context
   * @param expressionIndex Which of the expressions to use (for multi-round effects)
   */
  static toImpulse(
    effectItem: BaseEffectItem,
    parseContext: SetRequired<ExpressionParseContext, "rounds" | "targets" | "resources" | "indices">,
    expressionIndex = 0
  ) {
    const expression = effectItem.impulseExpressions[expressionIndex];
    if (expression == null) return null;

    let pointsToAdd: number | null = null;
    let valueToSet: number | null = null;

    const randomValue = Math.random();

    if (effectItem.activatorId == null || effectItem.affectedId == -1) {
      return null;
    }

    try {
      const calculated = calculateExpression(expression, {
        ...parseContext,
        fixedRandom: randomValue,
        affectedId: effectItem.affectedId,
        affectedType: effectItem.affectedType
      });

      // depending on the type of operation, mark the value
      // as value to add or to set
      if (expression[0] == "=") {
        valueToSet = Number(calculated.value);
      } else {
        pointsToAdd = Number(calculated.value);
      }

      // if it can be activated, create an impulse for the item
      const impulse: ImpulseItem = {
        pointsToAdd,
        valueToSet,
        sourceValues: [
          {
            type: effectItem.activatorType,
            id: effectItem.activatorId,
            effectId: effectItem.id,
            effectType: "effect",
            value: pointsToAdd,
            valueToSet,
            randomValue
          }
        ]
      };
      return impulse;
    } catch (error) {
      console.warn(error);
      return null;
    }
  }

  static isSpecial(effect: { impulseExpressions: string[] }) {
    return effect.impulseExpressions.some(Effect.isSpecialExpression);
  }
  static isSpecialExpression(impulseExpression: string) {
    return /[^\d*/+-=.\s()]/g.test(impulseExpression);
  }

  /**
   * Parse and sum up a group of expressions.
   * (only works with basic expressions)
   */
  static sumExpressions(expressions: string[]) {
    if (expressions.some(Effect.isSpecialExpression)) return NaN;
    return expressions.reduce(
      (acc, expression) =>
        acc + Number(calculateExpression(expression, {}).value),
      0
    );
  }

  /**
   * @param effectItems
   * @param activator
   */
  static filterByActivator(
    effectItems: { [effectId: number]: EffectItem },
    activator: { type: string; id: number }
  ) {
    return Object.values(effectItems).filter(
      (effect) =>
        effect.activatorType === activator?.type &&
        effect.activatorId === activator?.id
    );
  }
}

/**
 * Join two effects to each other.
 * These effects do NOT accurately represent the sum of the two effects!
 * The joining is purely VISUAL, to be used in rendered lists.
 * @param effectA
 * @param effectB
 */
export function joinEffects(
  effectA: BaseEffectItem | JoinedBaseEffectItem,
  effectB: BaseEffectItem | JoinedBaseEffectItem | null
): JoinedBaseEffectItem {
  if (effectB == null) {
    if (isJoinedEffect(effectA)) return effectA;
    return {
      key: effectA.id.toString(),
      type: "joinedEffect",
      effectIds: [effectA.id],
      affectedId: effectA.affectedId,
      affectedType: effectA.affectedType,
      activatorIds: [effectA.activatorId],
      activatorTypes: [effectA.activatorType],
      impulseExpressions: effectA.impulseExpressions,
      visibility: effectA.visibility,
      hint: effectA.hint
    };
  }

  if (
    effectA.affectedId != effectB.affectedId ||
    effectA.affectedType != effectB.affectedType
  ) {
    throw new Error("can't add two effects for seperate targets");
  }

  const l = Math.max(
    effectA.impulseExpressions.length,
    effectB.impulseExpressions.length
  );
  const combinedImpulseExpressions = Array(l)
    .fill(0)
    .map((_, i) => {
      const expressionA: string = effectA.impulseExpressions?.[i];
      const expressionB: string = effectB.impulseExpressions?.[i];

      if (!expressionB) return expressionA;
      if (!expressionA) return expressionB;

      if (expressionB.startsWith("=")) {
        if (expressionA.startsWith("=")) return expressionB;
        return `${expressionB}${expressionA}`;
      }
      if (expressionA.startsWith("-")) {
        return `+(${expressionA})${expressionB}`;
      }
      return `${expressionA}${expressionB}`;
    });
  
  const effectIdsA = isJoinedEffect(effectA) ? effectA.effectIds : [effectA.id];
  const effectIdsB = isJoinedEffect(effectB) ? effectB.effectIds : [effectB.id];
  const activatorIdsA = isJoinedEffect(effectA) ? effectA.activatorIds : [effectA.activatorId];
  const activatorIdsB = isJoinedEffect(effectB) ? effectB.activatorIds : [effectB.activatorId];
  const activatorTypesA = isJoinedEffect(effectA) ? effectA.activatorTypes : [effectA.activatorType];
  const activatorTypesB = isJoinedEffect(effectB) ? effectB.activatorTypes : [effectB.activatorType];
  
  const joined: JoinedBaseEffectItem = {
    key: effectIdsA.join(",") + effectIdsB.join(","),
    type: "joinedEffect",
    effectIds: [...effectIdsA, ...effectIdsB],
    affectedId: effectA.affectedId,
    affectedType: effectA.affectedType,
    activatorIds: [...activatorIdsA, ...activatorIdsB],
    activatorTypes: [...activatorTypesA, ...activatorTypesB],
    impulseExpressions: combinedImpulseExpressions,
    visibility: "visible",
    hint: "",
  };

  if (effectA.visibility == "hidden" && effectB.visibility == "hidden") {
    joined.visibility = "hidden";
    joined.hint = "";
    return joined;
  } else if (effectA.visibility == "hidden") {
    joined.visibility = effectB.visibility;
    joined.hint = effectB.hint;
    joined.impulseExpressions = effectB.impulseExpressions;
    return joined;
  } else if (effectB.visibility == "hidden") {
    joined.visibility = effectA.visibility;
    joined.hint = effectA.hint;
    joined.impulseExpressions = effectA.impulseExpressions;
    return joined;
  }

  let visibility: JoinedBaseEffectItem["visibility"] = "visible";
  let hint = "";
  let impulseExpressions: string[] = [""];
  if (effectA.visibility == "visible" && effectB.visibility == "visible") {
    visibility = "visible";
    impulseExpressions = combinedImpulseExpressions;
  } else if (effectA.visibility === "hint" && effectB.visibility === "hint") {
    visibility = "hint";
    hint = effectA.hint && effectB.hint ? effectA.hint + "," + effectB.hint : effectA.hint || effectB.hint;
  } else if (effectA.visibility == "visible" && effectB.visibility == "hint") {
    visibility = "mixed";
    impulseExpressions = effectA.impulseExpressions;
    hint = effectB.hint;
  } else if (effectA.visibility == "hint" && effectB.visibility == "visible") {
    visibility = "mixed";
    hint = effectA.hint;
    impulseExpressions = effectB.impulseExpressions;
  } else if (effectA.visibility == "mixed" && effectB.visibility == "hint") {
    visibility = "mixed";
    hint = effectA.hint;
    if (effectB.hint) hint += "," + effectB.hint;
    impulseExpressions = effectA.impulseExpressions;
  } else if (effectA.visibility == "mixed" && effectB.visibility == "visible") {
    visibility = "mixed";
    hint = effectA.hint;
    impulseExpressions = combinedImpulseExpressions;
  } else if (effectA.visibility == "hint" && effectB.visibility == "mixed") {
    visibility = "mixed";
    hint = effectA.hint;
    if (effectB.hint) hint += "," + effectB.hint;
    impulseExpressions = effectB.impulseExpressions;
  } else if (effectA.visibility == "visible" && effectB.visibility == "mixed") {
    visibility = "mixed";
    hint = effectB.hint;
    impulseExpressions = combinedImpulseExpressions;
  } else if (effectA.visibility == "mixed" && effectB.visibility == "mixed") {
    visibility = "mixed";
    hint = effectA.hint;
    if (effectB.hint) hint += "," + effectB.hint;
    impulseExpressions = combinedImpulseExpressions;
  }

  joined.visibility = visibility;
  joined.hint = hint;
  joined.impulseExpressions = impulseExpressions;

  return joined;
}

function isJoinedEffect(effect: BaseEffectItem | JoinedBaseEffectItem): effect is JoinedBaseEffectItem {
  return effect.type === "joinedEffect";
}

/***
 * Add two effects to each other.
 */
export function addEffects(effectA: BaseEffectItem, effectB: BaseEffectItem): BaseEffectItem {
  if (
    effectA.affectedId != effectB.affectedId ||
    effectA.affectedType != effectB.affectedType
  ) {
    throw new Error("can't add two effects for seperate targets");
  }

  const l = Math.max(
    effectA.impulseExpressions.length,
    effectB.impulseExpressions.length
  );
  const combinedImpulseExpressions = Array(l)
    .fill(0)
    .map((_, i) => {
      const expressionA: string = effectA.impulseExpressions?.[i];
      const expressionB: string = effectB.impulseExpressions?.[i];

      if (!expressionB) return expressionA;
      if (!expressionA) return expressionB;

      if (expressionB.startsWith("=")) {
        if (expressionA.startsWith("=")) return expressionB;
        return `${expressionB}${expressionA}`;
      }
      if (expressionA.startsWith("-")) {
        return `+(${expressionA})${expressionB}`;
      }
      return `${expressionA}${expressionB}`;
    });

  return {
    ...effectA,
    hint: "",
    visibility: "visible",
    createdAt: Date.now(),
    impulseExpressions: combinedImpulseExpressions
  };
}

export function valueToEffect(
  value: number,
  effectData: {
    activatorType: EffectItem["activatorType"];
    activatorId: EffectItem["activatorId"];
    affectedType: EffectItem["affectedType"];
    affectedId: EffectItem["affectedId"];
  } & Partial<EffectItem>
) {
  const expression = value < 0 ? `${value}` : `+${value}`;
  return expressionToEffect(expression, effectData);
}

export function expressionToEffect(expression: string, 
  effectData: {
  activatorType: EffectItem["activatorType"];
  activatorId: EffectItem["activatorId"];
  affectedType: EffectItem["affectedType"];
  affectedId: EffectItem["affectedId"];
} & Partial<EffectItem>) {
  const effect: EffectItem = {
    type: "effect",
    order: -1,
    docs: {},
    id: -1,
    uid: "effect_-1",
    createdAt: -1,
    hint: "",
    visibility: "visible",
    pendingEffectsVisible: true,
    pendingEffectsOpenable: true,

    ...effectData,
    impulseExpressions: [expression]
  };
  return effect;
}

export default Effect;
